import Vue from 'vue';
import VueRouter from 'vue-router';
import { permissionGuard, pendingPermissionChecks, checkPermissionAccess } from './permissionGuard';
import { EventBus } from '@/eventBus';
import { FEATURES } from '@/config/featureConfig';
import CallbackPage from '../views/CallbackPage.vue';
import SlackCallbackPage from '../views/SlackCallbackPage.vue';
import Settings from '../views/Settings.vue';
import Onboard from '../views/Onboard.vue';
import OnboardContact from '../views/OnboardContact.vue';
import OnboardMessaging from '../views/OnboardMessaging.vue';
import OnboardReport from '../views/OnboardReport.vue';
import OnboardPurpose from '../views/OnboardPurpose.vue';
import OnboardPricing from '../views/OnboardPricing.vue';
import Notification from '../views/Notification.vue';
import DataFileView from '../views/DataFileView.vue';
import SlackSuccessPage from '../views/SlackSuccessPage.vue';
import CustomReports from '../views/CustomReports.vue';
import CustomDashboards from '../views/CustomDashboards.vue';
import TemplateReports from '../views/TemplateReports.vue';
import TemplateReportPage from '../views/TemplateReport.vue';
import Subscription from '../views/Subscription.vue';
import SubscriptionConfirm from '../views/SubscriptionConfirm.vue';
import Dashboard from '../views/SystemDashboard.vue';
import MyDashboard from '../views/MyDashboard.vue';
import Users from '../views/Users.vue';
import DownloadManager from '../views/DownloadManager.vue';
import CardDesign from '../views/CardDesign.vue';
import ReportSelect from '../views/ReportSelect.vue';
import StatusPage from '../views/StatusPage.vue';
import DetailedDashboard from '../views/DetailedDashboard.vue';
import Home from '../views/Home.vue';
import LiveDemo from '../views/LiveDemo.vue';
import AutoSubscription from '../views/AutoSubscription.vue';
import OrderConfirm from '../views/OrderConfirm.vue';
import Plugins from '../views/Plugins.vue';
import Plugin from '../views/Plugin.vue';
import Oauth2Callback from '../views/Oauth2Callback.vue';
import ConsentScreen from '../views/oauth/ConsentScreen.vue';
import ProductSegmentDetail from '../views/ProductSegmentDetail.vue';
import ProductSegmentListing from '../views/ProductSegmentListing.vue';
import EtlStatusPage from '../views/EtlStatusPage.vue';
import ScheduleProfiles from '../views/ScheduleProfiles.vue';
import ScheduleProfile from '../views/ScheduleProfile.vue';
import Schedules from '../views/Schedules.vue';
import Schedule from '../views/Schedule.vue';
import ScheduleDetail from '../views/ScheduleDetail.vue';
import ProductSegmentExplore from '../views/ProductSegmentExplore.vue';
import ScheduleTemplateExplore from '../views/ScheduleTemplateExplore.vue';
import DashboardLibrary from '../views/DashboardLibrary.vue';
import ReportLibrary from '../views/ReportLibrary.vue';
import PremiumOnboarding from '../views/PremiumOnboarding.vue';
import QuickbooksOauth2Callback from '@/views/QuickbooksOauth2Callback.vue';
import SetupGuide from '../views/SetupGuide.vue';
import EmailTemplates from '../views/EmailTemplates.vue';
import EmailTemplate from '../views/EmailTemplate.vue';
import EmailTemplateNew from '../views/EmailTemplateNew.vue';
import EmailReceivers from '../views/EmailReceivers.vue';
import SpreadsheetTemplates from '../views/SpreadsheetTemplates.vue';
import SpreadsheetConnected from '../views/SpreadsheetConnected.vue';
import SpreadsheetClone from '../views/SpreadsheetClone.vue';
import SpreadsheetIntegration from '../views/SpreadsheetIntegration.vue';
import QuickbooksIntegration from '../views/QuickbooksIntegration.vue';
import GettingStarted from '../views/GettingStarted.vue';
import BuyingListDetail from '../views/buying-list/BuyingListDetail.vue';
import SupplierList from '../views/supplier/SupplierList.vue';
import SupplierDetail from '../views/supplier/SupplierDetail.vue';
import SupplierCreate from '../views/supplier/SupplierCreate.vue';
import BuyingLists from '../views/buying-list/BuyingLists.vue';
import BuyingListSuggestion from '../views/buying-list/BuyingListSuggestion.vue';
import BuyingTable from '../views/inventory/BuyingTable.vue';
import GoogleSpreadsheetTemplates from '../views/GoogleSpreadsheetTemplates.vue';
import GoogleSpreadsheetTemplateDetail from '../views/GoogleSpreadsheetTemplateDetail.vue';
import GoogleSpreadsheetFiles from '../views/GoogleSpreadsheetFiles.vue';
import GoogleSpreadsheetFileDetail from '../views/GoogleSpreadsheetFileDetail.vue';
import GoogleSpreadsheetIntroductionView from '../views/GoogleSpreadsheetIntroduction.vue';
import InventoryTransferSuggestions from '../views/InventoryTransferSuggestions.vue';
// import InventoryTransferManagement from '../views/InventoryTransferManagement.vue';
import PermissionInitError from '../views/PermissionInitError.vue';
import SupplierImportUpload from '../views/supplier/SupplierImportUpload.vue';
import PageNotFound from '../views/PageNotFound.vue';
import AutomationDashboard from '../views/automation-dashboard/index.vue';
import UnapprovedPOs from '../views/purchase-order/UnapprovedPOs.vue';
import AtlasSetup from '../views/AtlasSetup.vue';
import PermissionDenied from '../views/PermissionDenied.vue';
import PurchaseOrderList from '../views/purchase-order/PurchaseOrderList.vue';
import PurchaseOrderCreate from '../views/purchase-order/PurchaseOrderCreate.vue';
import PurchaseOrderDetail from '../views/purchase-order/PurchaseOrderDetail.vue';
import AutomatedPOConfig from '../views/purchase-order/AutomatedPOConfig.vue';
import PODashboard from '../views/purchase-order/PODashboard.vue';
const CustomColumnValues = () => import('../views/settings/CustomColumnValues.vue');
import { insightsRoutes } from './insights';

// Lazy-loaded Shopify Flow views (code-split for smaller initial bundle)
const ShopifyFlowSettings = () => import('../views/shopify-flow/ShopifyFlowSettings.vue');
const ShopifyFlowEvents = () => import('../views/shopify-flow/ShopifyFlowEvents.vue');
const ShopifyFlowTemplates = () => import('../views/shopify-flow/ShopifyFlowTemplates.vue');

Vue.use(VueRouter);

const routes = [
  {
    path: '/automation-dashboard',
    name: 'automationDashboard',
    component: AutomationDashboard,
    meta: {
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/shopify-flow',
    name: 'shopifyFlowSettings',
    component: ShopifyFlowSettings,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },
  {
    path: '/shopify-flow/events',
    name: 'shopifyFlowEvents',
    component: ShopifyFlowEvents,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },
  {
    path: '/shopify-flow/templates',
    name: 'shopifyFlowTemplates',
    component: ShopifyFlowTemplates,
    meta: {
      requiredPermission: FEATURES.SHOPIFY_FLOW_INTEGRATION,
    },
  },

  // Insights Hub routes (Professional Plan and above)
  ...insightsRoutes,
  {
    path: '/buying-calendar',
    name: 'buyingListSuggestion',
    component: BuyingListSuggestion,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/slack/callback',
    name: 'slackCallback',
    component: SlackCallbackPage,
  },
  {
    path: '/shopify/callback',
    name: 'callback',
    component: CallbackPage,
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
  },
  {
    path: '/settings/:tab',
    name: 'settingTabs',
    component: Settings,
  },
  {
    path: '/settings/products/custom-column-values',
    name: 'custom-column-values',
    component: CustomColumnValues,
    meta: {
      title: 'Custom Column Values',
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/',
    name: 'home',
    component: Home,
    props: true,
  },
  {
    path: '/atlas-setup',
    name: 'atlasSetup',
    component: AtlasSetup,
    meta: {
      title: 'Atlas Setup',
    },
  },
  {
    path: '/onboard',
    name: 'onboard',
    component: Onboard,
  },
  {
    path: '/onboard-contact',
    name: 'onboardContact',
    component: OnboardContact,
  },
  {
    path: '/onboard-messaging',
    name: 'onboardMessaging',
    component: OnboardMessaging,
  },
  {
    path: '/onboard-report',
    name: 'onboardReport',
    component: OnboardReport,
  },
  {
    path: '/onboard-purpose',
    name: 'onboardPurpose',
    component: OnboardPurpose,
  },
  {
    path: '/onboard-pricing',
    name: 'onboardPricing',
    component: OnboardPricing,
  },
  {
    path: '/subscription/confirm',
    name: 'subscriptionConfirm',
    component: SubscriptionConfirm,
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: Subscription,
  },
  {
    path: '/files/:file_name',
    name: 'dataFileView',
    component: DataFileView,
  },
  {
    path: '/slack/success',
    name: 'slackSuccessPage',
    component: SlackSuccessPage,
  },
  {
    path: '/reports',
    name: 'templateReports',
    component: TemplateReports,
    props: true,
  },
  {
    path: '/report-library',
    name: 'reportLibrary',
    component: ReportLibrary,
    meta: {
      title: 'Report Library',
    },
  },
  {
    path: '/reports/measures/:measure_code',
    name: 'detailReportPage',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/analytics/:measure_code/:report_name',
    name: 'detailReportByName',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/reports/schedule/:preview',
    name: 'previewScheduledReport',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/my-reports',
    name: 'customReports',
    component: CustomReports,
  },
  {
    path: '/ai-report-builder',
    name: 'reportBuilder',
    component: () => import('../views/ReportBuilder.vue'),
  },
  {
    path: '/purchase-assistant',
    name: 'purchaseAssistant',
    component: () => import('../views/PurchaseAssistant.vue'),
    meta: {
      requiredPermission: FEATURES.PAID_MEASURES,
    },
  },
  {
    path: '/my-dashboards',
    name: 'customDashboards',
    component: CustomDashboards,
    props: true,
  },
  {
    path: '/my-reports/:custom_report_id',
    name: 'customReport',
    component: TemplateReportPage,
  },
  {
    path: '/reports/:type_id/:type_name',
    name: 'templateReportsByTypes',
    component: TemplateReports,
    props: true,
  },
  {
    path: '/reports/:type_id/:type_name/:template_report_id',
    name: 'templateReportByType',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/reports/:template_report_id',
    name: 'templateReport',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '/schedule',
    name: 'schedule',
    component: Notification,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/edit/:schedule_id/report/:report_index',
    name: 'scheduleEditReport',
    component: TemplateReportPage,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/card/view/:card_id',
    name: 'viewCardReport',
    component: TemplateReportPage,
    props: true,
  },
  {
    path: '*',
    redirect: '/',
  },
  {
    path: '/myboard/:dashboard_id',
    name: 'myDashboard',
    component: MyDashboard,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/my-board/:dashboard_id',
    name: 'myBoard',
    component: MyDashboard,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/users',
    name: 'users',
    component: Users,
    meta: {
      requiredPermission: FEATURES.USER_MANAGEMENT,
    },
  },
  {
    path: '/download-manager',
    name: 'downloadManager',
    component: DownloadManager,
  },
  {
    path: '/design-card/:id',
    name: 'cardDesign',
    component: CardDesign,
    meta: {
      requiredPermission: FEATURES.CUSTOM_DASHBOARD,
    },
  },
  {
    path: '/report-select',
    name: 'reportSelect',
    component: ReportSelect,
  },
  {
    path: '/status/:code',
    name: 'statusPage',
    component: StatusPage,
  },
  {
    path: '/public-board/:dashboard_id',
    name: 'publicBoard',
    component: Dashboard,
  },
  {
    path: '/board/:dashboard_id/:type',
    name: 'dashboardType',
    component: Dashboard,
  },
  {
    path: '/board/:dashboard_id',
    name: 'dashboard',
    component: Dashboard,
  },
  {
    path: '/dashboard/product',
    name: 'productDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/store',
    name: 'storeDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/vendor',
    name: 'vendorDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/dashboard/collection',
    name: 'collectionDashboard',
    component: DetailedDashboard,
  },
  {
    path: '/live-demo',
    name: 'liveDemo',
    component: LiveDemo,
  },
  {
    path: '/premium-onboarding',
    name: 'PremiumOnboarding',
    component: PremiumOnboarding,
  },
  {
    path: '/plugins',
    name: 'plugins',
    component: Plugins,
  },
  {
    path: '/plugin/:pluginId',
    name: 'plugin',
    component: Plugin,
  },
  {
    path: '/auto_subscription',
    name: 'autoSubscription',
    component: AutoSubscription,
  },
  {
    path: '/order_confirm',
    name: 'orderConfirm',
    component: OrderConfirm,
  },
  {
    path: '/oauth2callback',
    name: 'oauth2Callback',
    component: Oauth2Callback,
  },
  {
    path: '/quickbooks/oauth2callback',
    name: 'quickbooks/oauth2callback',
    component: QuickbooksOauth2Callback,
  },
  {
    path: '/oauth/consent',
    name: 'consentScreen',
    component: ConsentScreen,
  },
  {
    path: '/product-segment/:segment_id/type/:type',
    name: 'productSegmentDetail',
    component: ProductSegmentDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/product/explore',
    name: 'productExplore',
    component: ProductSegmentExplore,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/product-segment',
    name: 'productSegmentListing',
    component: ProductSegmentListing,
    props: true,
    meta: {
      requiredPermission: FEATURES.PRODUCT_SEGMENTATION,
    },
  },
  {
    path: '/status-page',
    name: 'etlStatusPage',
    component: EtlStatusPage,
    props: true,
  },
  {
    path: '/importing-status',
    name: 'etlStatusPage',
    component: EtlStatusPage,
    props: true,
  },
  {
    path: '/schedule/profiles',
    name: 'scheduleProfiles',
    component: ScheduleProfiles,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/profile/:profile_id',
    name: 'scheduleProfileEdit',
    component: ScheduleProfile,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/profile',
    name: 'scheduleProfileCreate',
    component: ScheduleProfile,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedules',
    name: 'scheduleListing',
    component: Schedules,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/edit/:schedule_id',
    name: 'scheduleEdit',
    component: Schedule,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/create',
    name: 'scheduleCreate',
    component: Schedule,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/templates',
    name: 'scheduleTemplateExplore',
    component: ScheduleTemplateExplore,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/schedule/:schedule_id',
    name: 'scheduleDetail',
    component: ScheduleDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.SCHEDULE_MANAGEMENT,
    },
  },
  {
    path: '/library/dashboard',
    name: 'DashboardLibrary',
    component: DashboardLibrary,
    props: true,
  },
  {
    path: '/setup-guide/:step',
    name: 'SetupGuide',
    component: SetupGuide,
    props: true,
  },
  {
    path: '/email-templates',
    name: 'EmailTemplates',
    component: EmailTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-templates/new',
    name: 'EmailTemplateNew',
    component: EmailTemplateNew,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-templates/:template_id',
    name: 'EmailTemplate',
    component: EmailTemplate,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/email-receivers',
    name: 'EmailReceivers',
    component: EmailReceivers,
    props: true,
    meta: {
      requiredPermission: FEATURES.EMAIL_TEMPLATES,
    },
  },
  {
    path: '/spreadsheet-templates',
    name: 'SpreadsheetTemplates',
    component: SpreadsheetTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-connected',
    name: 'SpreadsheetConnected',
    component: SpreadsheetConnected,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-clone/:id',
    name: 'SpreadsheetClone',
    component: SpreadsheetClone,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/spreadsheet-integration',
    name: 'SpreadsheetIntegration',
    component: SpreadsheetIntegration,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/quickbooks-integration',
    name: 'QuickbooksIntegration',
    component: QuickbooksIntegration,
    props: true,
    meta: {
      requiredPermission: FEATURES.QUICKBOOKS_INTEGRATION,
    },
  },
  {
    path: '/getting-started',
    name: 'GettingStarted',
    component: GettingStarted,
    props: true,
  },

  {
    path: '/suppliers',
    name: 'suppliers',
    component: SupplierList,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/suppliers/import/upload',
    name: 'SupplierImportUpload',
    component: SupplierImportUpload,
    meta: {
      requiredPermission: FEATURES.SUPPLIER_MANAGEMENT,
    },
  },
  {
    path: '/suppliers/:id',
    name: 'supplier',
    component: SupplierDetail,
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/supplier/create',
    name: 'supplierCreate',
    component: SupplierCreate,
    meta: {
      requiredPermission: FEATURES.PAID_MEASURES,
    },
  },
  {
    path: '/suppliers/:supplierId/purchase-orders/create',
    name: 'createPurchaseOrder',
    component: PurchaseOrderCreate,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/buying-lists/:id',
    name: 'buyingListDetail',
    component: BuyingListDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
    },
  },
  {
    path: '/buying-lists',
    name: 'buying-lists',
    component: BuyingLists,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Buying Lists',
    },
  },
  {
    path: '/buying-table',
    name: 'buyingTable',
    component: BuyingTable,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Buying Table',
    },
  },
  {
    path: '/inventory-transfers/suggestions',
    name: 'inventoryTransferSuggestions',
    component: InventoryTransferSuggestions,
    meta: {
      requiresAuth: true,
      requiredPermission: FEATURES.REPLENISH_FROM_WAREHOUSE,
      title: 'Inventory Transfer Suggestions',
    },
  },
  // {
  //   path: '/inventory-transfers',
  //   name: 'inventoryTransferManagement',
  //   component: InventoryTransferManagement,
  //   meta: {
  //     requiresAuth: true,
  //     requiredPermission: FEATURES.REPLENISH_FROM_WAREHOUSE,
  //     title: 'Transfer Management',
  //   },
  // },
  {
    path: '/google-spreadsheet-introduction',
    name: 'GoogleSpreadsheetIntroduction',
    component: GoogleSpreadsheetIntroductionView,
  },
  {
    path: '/google-spreadsheet-templates',
    name: 'GoogleSpreadsheetTemplates',
    component: GoogleSpreadsheetTemplates,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-templates/:id',
    name: 'GoogleSpreadsheetTemplateDetail',
    component: GoogleSpreadsheetTemplateDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-files',
    name: 'GoogleSpreadsheetFiles',
    component: GoogleSpreadsheetFiles,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/google-spreadsheet-files/:id',
    name: 'GoogleSpreadsheetFileDetail',
    component: GoogleSpreadsheetFileDetail,
    meta: {
      requiredPermission: FEATURES.ADVANCED_SPREADSHEET_INTEGRATION,
    },
  },
  {
    path: '/purchase-orders/dashboard',
    name: 'po-dashboard',
    component: PODashboard,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Order Dashboard',
    },
  },
  {
    path: '/purchase-orders',
    name: 'purchase-orders',
    component: PurchaseOrderList,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Orders',
    },
  },
  {
    path: '/purchase-orders/unapproved',
    name: 'unapproved-pos',
    component: UnapprovedPOs,
    meta: {
      title: 'Unapproved Purchase Orders',
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/purchase-orders/:id',
    name: 'purchase-order-detail',
    component: PurchaseOrderDetail,
    props: true,
    meta: {
      requiredPermission: FEATURES.OPERATIONAL_MODE,
      title: 'Purchase Order Details',
    },
  },
  {
    path: '/automation-configs',
    name: 'AutomatedPOConfig',
    component: AutomatedPOConfig,
    meta: {
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  {
    path: '/automated-po-overview',
    name: 'automated-po-overview',
    component: () => import('@/views/automation-overview/AutomatedPOOverview.vue'),
    meta: {
      title: 'Automated Purchase Orders - Get Started',
      requiredPermission: FEATURES.AUTOMATED_PURCHASE_ORDER,
    },
  },
  // {
  //   path: '/purchase-order/automation/:id',
  //   name: 'AutomatedPOConfigDetail',
  //   component: AutomatedPOConfigDetail,
  //   props: true,
  //   meta: {
  //     permissions: ['purchase_order_write'],
  //   },
  // },
  // {
  //   path: '/purchase-order/automation/create',
  //   name: 'AutomatedPOConfigCreate',
  //   component: AutomatedPOConfigCreate,
  //   props: true,
  //   meta: {
  //     permissions: ['purchase_order_write'],
  //   },
  // },
  {
    path: '/premium_reports/:premium/:type_id/:type_name',
    name: 'premiumTemplateReportsByTypes',
    component: TemplateReports,
  },
  {
    path: '/permission-denied',
    name: 'permission-denied',
    component: PermissionDenied,
    meta: {
      title: 'Permission Denied',
    },
  },
  {
    path: '/permission-init-error',
    name: 'permissionInitError',
    component: PermissionInitError,
    meta: {
      title: 'Permission Initialization Error',
    },
  },
  {
    path: '/purchase-orders/create',
    name: 'createPurchaseOrder',
    component: PurchaseOrderCreate,
    props: true,
    meta: {
      // Add relevant permissions if needed
      // requiredPermission: FEATURES.PURCHASE_ORDER_MANAGEMENT
    },
  },
  {
    path: '/suppliers/actions/import',
    name: 'supplierImport',
    component: SupplierImportUpload,
    props: true,
    meta: {
      // Add relevant permissions if needed, e.g., for managing suppliers
      // requiredPermission: FEATURES.SUPPLIER_MANAGEMENT
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Use $once to ensure the listener runs only once
EventBus.$once('permissions-ready', () => {

  // Copy the array to avoid issues if it's modified during iteration
  const checksToProcess = [...pendingPermissionChecks];
  const currentRoute = router.currentRoute; // Get current route once


  let needsRedirect = false;
  let redirectInfo = { feature: '', fullPath: '' };

  checksToProcess.forEach((pendingCheck) => {

    const hasPermission = checkPermissionAccess(pendingCheck.requiredPermission, pendingCheck.to);

    if (!hasPermission) {

      // Check if this denied route is the one the user is currently on
      if (currentRoute.path === pendingCheck.to.path || currentRoute.path === pendingCheck.to.path.split('?')[0]) {
          needsRedirect = true;
          redirectInfo = {
              feature: pendingCheck.requiredPermission,
              fullPath: pendingCheck.to.fullPath,
          };
      }

      // Emit event even if it's not the current route (useful for logging/debugging)
      EventBus.$emit('permission-denied', {
        feature: pendingCheck.requiredPermission,
        route: pendingCheck.to,
      });
    }
  });

  // Clear the original pending checks array *after* processing all checks
  pendingPermissionChecks.length = 0;

  // Perform redirect *after* the loop if necessary
  if (needsRedirect) {
      router.replace({
          path: '/permission-denied',
          query: {
              feature: redirectInfo.feature,
              redirect: redirectInfo.fullPath,
          },
      });
  }
});

// Listen for permissions-failed event and redirect to error page
EventBus.$once('permissions-failed', () => {
  router.replace({ path: '/permission-init-error' });
});

router.beforeEach(permissionGuard);

export default router;
